import "./Products.css";
import { Routes, Route, useNavigate } from "react-router-dom";
import { useRef, useState, useEffect } from "react";
import platformSolutions from "./platformSolutions";
import ProductPage from "./ProductPage";
import platform from "./icons/platform.png";
import {
    FaArrowRight,
    FaUsers,
    FaBolt,
    FaChartBar,
    FaClipboardList,
    FaChartLine,
    FaExpandArrowsAlt,
    FaCogs,
    FaHeadset,
    FaChevronLeft,
    FaChevronRight,
} from "react-icons/fa";
import Header from './Header';
import Footer from './Footer';

function HomePage() {
    const navigate = useNavigate();
    const productsRef = useRef(null);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [maxScroll, setMaxScroll] = useState(0);
    const [cardWidth, setCardWidth] = useState(0);

    // Update scroll state
    const handleScroll = () => {
        const container = productsRef.current;
        if (container) {
            setScrollPosition(container.scrollLeft);
            setMaxScroll(container.scrollWidth - container.clientWidth);
        }
    };

    // Get active dot index
    const getActiveDot = () => {
        const container = productsRef.current;
        if (!container) return 0;
        const firstCard = container.querySelector('.hp-product-card');
        if (!firstCard) return 0;
        const width = firstCard.offsetWidth + 30; // card width + gap
        const index = Math.round(container.scrollLeft / width);
        const total = platformSolutions.length;
        return Math.min(Math.max(index, 0), total - 1);
    };

    // Scroll left
    const scrollLeft = () => {
        const container = productsRef.current;
        if (container) {
            const width = container.querySelector('.hp-product-card')?.offsetWidth || 300;
            container.scrollBy({ left: -(width + 30), behavior: 'smooth' });
        }
    };

    // Scroll right
    const scrollRight = () => {
        const container = productsRef.current;
        if (container) {
            const width = container.querySelector('.hp-product-card')?.offsetWidth || 300;
            container.scrollBy({ left: width + 30, behavior: 'smooth' });
        }
    };

    // Scroll to specific dot
    const scrollToIndex = (index) => {
        const container = productsRef.current;
        if (container) {
            const firstCard = container.querySelector('.hp-product-card');
            if (firstCard) {
                const width = firstCard.offsetWidth + 30;
                container.scrollTo({ left: index * width, behavior: 'smooth' });
            }
        }
    };

    // Update maxScroll on resize
    useEffect(() => {
        const container = productsRef.current;
        if (container) {
            setMaxScroll(container.scrollWidth - container.clientWidth);
            const firstCard = container.querySelector('.hp-product-card');
            if (firstCard) {
                setCardWidth(firstCard.offsetWidth + 30);
            }
        }
        window.addEventListener('resize', handleScroll);
        return () => window.removeEventListener('resize', handleScroll);
    }, []);

    const activeDot = getActiveDot();
    const totalDots = platformSolutions.length;

    return (
        <div className="hp-overall-continer">
            <Header />
            {/* hero page finishes */}
            <section className="hp-hero">
                <div className="hp-hero-top">
                    <div className="hp-hero-left">
                        <div className="hp-logobox">
                            {/* <img src={image1} alt="alvanim image" className="logo" />
              <h2>Alvanium</h2> */}
                        </div>
                        <div className="hp-text-continer">
                            <h1 className="hp-hero-content">
                                Powerful Apps for
                            </h1>
                            <h1 className="hp-gradient-text">Smarter Workflows</h1>
                            <h5 className="hp-Sub-Heading">
                                Explore our apps for Jira, Confluence, Jira Service Management, Zendesk, and Monday.com to improve productivity and simplify workflows.
                            </h5>
                            <p className="hp-Short-Description">
                                Alvanium builds business apps that help teams streamline workflows, improve collaboration, and work more efficiently.
                            </p>
                            <div className="hp-but-con">
                                <button
                                    className="hp-hero-but-2"
                                    onClick={() => navigate('/contact')}
                                >
                                    Get Started
                                    <FaArrowRight className="icon" />
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="hp-hero-right">
                        <div className="hp-hero-image">
                            <img src={platform} className="hp-platform" alt="Platform" />
                        </div>
                    </div>
                </div>
                <div className="hp-hero-botum">
                    <div className="hp-feature-box">
                        <div className="hp-feature-icon">
                            <FaUsers />
                        </div>
                        <div className="hp-feature-content">
                            <h3>Team Collaboration</h3>
                            <p>Work together seamlessly in real-time.</p>
                        </div>
                    </div>
                    <div className="hp-feature-box">
                        <div className="hp-feature-icon">
                            <FaBolt />
                        </div>
                        <div className="hp-feature-content">
                            <h3>Workflow Automation</h3>
                            <p>Automate tasks and save valuable time.</p>
                        </div>
                    </div>
                    <div className="hp-feature-box">
                        <div className="hp-feature-icon">
                            <FaChartBar />
                        </div>
                        <div className="hp-feature-content">
                            <h3>Smart Analytics</h3>
                            <p>Track performance and make data-driven decisions.</p>
                        </div>
                    </div>
                    <div className="hp-feature-box">
                        <div className="hp-feature-icon">
                            <FaClipboardList />
                        </div>
                        <div className="hp-feature-content">
                            <h3>Project Boards</h3>
                            <p>Organize tasks, assign work, and track project progress.</p>
                        </div>
                    </div>
                </div>
            </section>
            {/* Products Overview Section */}
            <section className="hp-products">
                <div className="hp-products-heading">
                    <h2>Our App <p> Development Services</p></h2>
                    <p>
                        We build custom apps for Jira, Confluence, Jira Service Management, Zendesk, and Monday.com to simplify workflows and improve productivity.
                    </p>
                </div>

                {/* Product Carousel with Navigation */}
                <div className="hp-carousel-wrapper">
                    {/* Left Arrow */}
                    <button
                        className={`hp-carousel-arrow hp-carousel-left ${scrollPosition <= 0 ? 'hp-arrow-disabled' : ''}`}
                        onClick={scrollLeft}
                        aria-label="Scroll left"
                    >
                        <FaChevronLeft />
                    </button>

                    {/* Cards Container */}
                    <div
                        className="hp-products-grid"
                        ref={productsRef}
                        onScroll={handleScroll}
                    >
                        {platformSolutions.map((item, index) => (
                            <div
                                className="hp-product-card"
                                key={index}
                                style={{ "--clr": item.color }}
                            >
                                <div className="hp-card-top">
                                    <div className="hp-card-icon">
                                        <img src={item.image} alt="img" />
                                    </div>
                                    <div className="hp-card-number">{item.number}</div>
                                </div>

                                <h3>{item.title}</h3>
                                <p>{item.description}</p>

                                <button
                                    className="hp-explore-btn"
                                    onClick={() => {
                                        navigate(`/product/${item.id}`);
                                    }}
                                >
                                    Explore Apps
                                    <FaArrowRight />
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* Right Arrow */}
                    <button
                        className={`hp-carousel-arrow hp-carousel-right ${scrollPosition >= maxScroll - 1 ? 'hp-arrow-disabled' : ''}`}
                        onClick={scrollRight}
                        aria-label="Scroll right"
                    >
                        <FaChevronRight />
                    </button>
                </div>

                {/* Dot Indicators */}
                <div className="hp-dots-container">
                    {Array.from({ length: totalDots }).map((_, index) => (
                        <button
                            key={index}
                            className={`hp-dot ${index === activeDot ? 'hp-dot-active' : ''}`}
                            onClick={() => scrollToIndex(index)}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>
            </section>
            {/* Benefits section */}
            <section className="hp-benefits-contin">
                <h1>Why Choose <p>Our apps</p></h1>
                <div className="hp-benefits-grid">
                    <div className="hp-benefit-card">
                        <div className="hp-benefit-icon">
                            <FaChartLine />
                        </div>
                        <h3>Improved Productivity</h3>
                        <p>Reduce manual work and improve efficiency.</p>
                    </div>

                    <div className="hp-benefit-card">
                        <div className="hp-benefit-icon">
                            <FaUsers />
                        </div>
                        <h3>Better Collaboration</h3>
                        <p>Enable teams to work together effectively.</p>
                    </div>

                    <div className="hp-benefit-card">
                        <div className="hp-benefit-icon">
                            <FaExpandArrowsAlt />
                        </div>
                        <h3>Scalable Systems</h3>
                        <p>Solutions designed for future growth.</p>
                    </div>

                    <div className="hp-benefit-card">
                        <div className="hp-benefit-icon">
                            <FaCogs />
                        </div>
                        <h3>Customized Experience</h3>
                        <p>Solutions aligned to business requirements.</p>
                    </div>

                    <div className="hp-benefit-card">
                        <div className="hp-benefit-icon">
                            <FaHeadset />
                        </div>
                        <h3>Reliable Support</h3>
                        <p>Continuous support and optimization.</p>
                    </div>
                </div>
            </section>
            {/* CTA Section */}
            <section className="hp-cta-section">
                <div className="hp-cta-content">
                    <h2>
                        Build Smarter Business
                        <br />
                        <span>Applications</span>
                    </h2>

                    <p>
                        Discover custom apps and scalable software that improve productivity and simplify workflows.
                    </p>

                    <div className="hp-cta-buttons">
                        <button
                            className="hp-cta-btn hp-secondary-btn"
                            onClick={() => navigate('/contact')}
                        >
                            Contact Us
                        </button>
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
}

function App() {
    return (
        <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/product/:id" element={<ProductPage />} />
        </Routes>
    );
}

export default App;